// src/components/agent/AgentAuthModal.jsx
// Partner sign in / sign up modal.
// Captcha: Cloudflare Turnstile, enabled only when REACT_APP_TURNSTILE_SITE_KEY is set
// (backend enforces it only when TURNSTILE_SECRET is set). A hidden honeypot field is always on.
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, X, CheckCircle2, Loader2, Briefcase } from "lucide-react";
import PhoneField, { normalisePhone, phoneError } from "../common/PhoneField";
import {
  registerAgent,
  loginAgent,
  saveAgentSession,
  partnerDashboardPath,
} from "../../utils/agentAuth";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

const TURNOVER_OPTIONS = [
  { value: "", label: "Prefer not to say" },
  { value: "below_10l", label: "Below ₹10 lakh" },
  { value: "10l_50l", label: "₹10 lakh to ₹50 lakh" },
  { value: "50l_1cr", label: "₹50 lakh to ₹1 crore" },
  { value: "1cr_5cr", label: "₹1 crore to ₹5 crore" },
  { value: "above_5cr", label: "Above ₹5 crore" },
];

const EMPTY_SIGNUP = {
  name: "",
  email: "",
  phone: "",
  is_registered_business: false,
  business_name: "",
  password: "",
  annual_turnover: "",
  notes: "",
};

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/* ── Turnstile widget (renders nothing when no site key) ─────────────────── */
let turnstileScriptPromise = null;
function loadTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!turnstileScriptPromise) {
    turnstileScriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      s.async = true;
      s.onload = () => resolve(window.turnstile);
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }
  return turnstileScriptPromise;
}

function Captcha({ onToken, resetKey }) {
  const ref = useRef(null);
  const widgetId = useRef(null);
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return undefined;
    let cancelled = false;
    loadTurnstile()
      .then((ts) => {
        if (cancelled || !ref.current) return;
        widgetId.current = ts.render(ref.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (t) => onToken(t),
          "expired-callback": () => onToken(""),
          "error-callback": () => onToken(""),
        });
      })
      .catch(() => onToken(""));
    return () => {
      cancelled = true;
      try {
        if (widgetId.current != null) window.turnstile?.remove(widgetId.current);
      } catch {
        /* ignore */
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);
  if (!TURNSTILE_SITE_KEY) return null;
  return <div ref={ref} className="min-h-[65px] flex justify-center" />;
}

/* ── small UI bits ───────────────────────────────────────────────────────── */
const inputCls =
  "w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-[15px] text-slate-900 " +
  "placeholder:text-slate-400 outline-none focus:border-[#FF4E01] focus:ring-2 focus:ring-[#FF4E01]/20";

function Field({ label, optional, error, children }) {
  return (
    <label className="block">
      <span className="mb-1 flex items-center gap-1.5 text-[13px] font-semibold text-slate-700">
        {label}
        {optional && <span className="font-normal text-slate-400">(optional)</span>}
      </span>
      {children}
      {error && <span className="mt-1 block text-xs text-red-600">{error}</span>}
    </label>
  );
}

function PasswordInput({ value, onChange, autoComplete, placeholder }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input
        type={show ? "text" : "password"}
        className={inputCls + " pr-10"}
        value={value}
        onChange={onChange}
        autoComplete={autoComplete}
        placeholder={placeholder}
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600"
        aria-label={show ? "Hide password" : "Show password"}
      >
        {show ? <EyeOff size={18} /> : <Eye size={18} />}
      </button>
    </div>
  );
}

// Honeypot: off-screen, not focusable. Bots fill it, humans never see it.
function Honeypot({ value, onChange }) {
  return (
    <input
      type="text"
      name="website"
      tabIndex={-1}
      autoComplete="off"
      aria-hidden="true"
      value={value}
      onChange={onChange}
      style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
    />
  );
}

/* ── main modal ──────────────────────────────────────────────────────────── */
// initialEmail: prefill from an invite link (/partner-signup?email=...); note: a line shown above the form
export default function AgentAuthModal({ open, onClose, initialMode = "signin", initialEmail = "", note = "", afterAuth = null }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState(initialMode); // "signin" | "signup" | "success"
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaReset, setCaptchaReset] = useState(0);
  const [honeypot, setHoneypot] = useState("");

  const [signin, setSignin] = useState({ identifier: "", password: "" });
  const [signup, setSignup] = useState(EMPTY_SIGNUP);
  const [errors, setErrors] = useState({});
  const [createdId, setCreatedId] = useState("");

  useEffect(() => {
    if (open) {
      setMode(initialMode);
      setServerError("");
      setErrors({});
      if (initialEmail) {
        setSignup((f) => ({ ...f, email: f.email || initialEmail }));
        setSignin((f) => ({ ...f, identifier: f.identifier || initialEmail }));
      }
    }
  }, [open, initialMode, initialEmail]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && !busy && onClose?.();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, busy, onClose]);

  if (!open) return null;

  const captchaMissing = TURNSTILE_SITE_KEY && !captchaToken;
  const resetCaptcha = () => {
    setCaptchaToken("");
    setCaptchaReset((n) => n + 1);
  };

  const switchMode = (m) => {
    setMode(m);
    setServerError("");
    setErrors({});
    resetCaptcha();
  };

  const goToDashboard = (profile, justRegistered = false) => {
    onClose?.();
    if (afterAuth) { afterAuth(profile, justRegistered); return; }   // e.g. the claim page finishes the claim first
    navigate(partnerDashboardPath(profile.user_id), { state: { justRegistered } });
  };

  /* sign in */
  const handleSignin = async (e) => {
    e.preventDefault();
    setServerError("");
    if (!signin.identifier.trim() || !signin.password) {
      setServerError("Enter your user ID or email, and password");
      return;
    }
    if (captchaMissing) {
      setServerError("Please complete the captcha");
      return;
    }
    setBusy(true);
    const res = await loginAgent({
      identifier: signin.identifier.trim(),
      password: signin.password,
      captcha_token: captchaToken,
      website: honeypot,
    });
    setBusy(false);
    if (res.status === 200 && res.data?.token) {
      saveAgentSession(res.data);
      goToDashboard(res.data.profile);
    } else {
      setServerError(res.message || "Sign in failed. Please try again.");
      resetCaptcha();
    }
  };

  /* sign up */
  const validateSignup = () => {
    const er = {};
    if (signup.name.trim().length < 2) er.name = "Enter your full name";
    if (!EMAIL_RE.test(signup.email.trim())) er.email = "Enter a valid email";
    const ph = normalisePhone(signup.phone);
    if (!ph.valid) er.phone = phoneError(ph.country);
    if (signup.is_registered_business && signup.business_name.trim().length < 2)
      er.business_name = "Enter your registered business name";
    if (signup.password.length < 8) er.password = "At least 8 characters";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setServerError("");
    if (!validateSignup()) return;
    if (captchaMissing) {
      setServerError("Please complete the captcha");
      return;
    }
    setBusy(true);
    const res = await registerAgent({
      ...signup,
      name: signup.name.trim(),
      email: signup.email.trim().toLowerCase(),
      phone: normalisePhone(signup.phone).e164,   // always with the country code: +919876543210
      business_name: signup.is_registered_business ? signup.business_name.trim() : "",
      notes: signup.notes.trim(),
      captcha_token: captchaToken,
      website: honeypot,
    });
    setBusy(false);
    if (res.status === 200 && res.data?.token) {
      saveAgentSession(res.data); // logged in immediately
      setCreatedId(res.data.profile.user_id);
      setMode("success");
      setSignup(EMPTY_SIGNUP);
      setTimeout(() => goToDashboard(res.data.profile, true), 1600);
    } else {
      if (res.data?.errors?.length) setServerError(res.data.errors.join(". "));
      else setServerError(res.message || "Could not create your account. Please try again.");
      resetCaptcha();
    }
  };

  const setS = (k) => (e) =>
    setSignup((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const previewId = signup.email.includes("@") ? signup.email.trim().toLowerCase().split("@")[0] : "";

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center bg-slate-900/50 p-0 sm:p-4"
      onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose?.()}
      role="dialog"
      aria-modal="true"
      aria-label={mode === "signup" ? "Partner sign up" : "Partner sign in"}
    >
      <div className="relative w-full sm:max-w-[460px] max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl bg-white shadow-2xl">
        {/* header */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-100 bg-white px-5 py-4">
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-orange-50 text-[#FF4E01]">
              <Briefcase size={18} />
            </span>
            <div>
              <h2 className="m-0 text-[17px] font-bold text-slate-900">
                {mode === "signup" ? "Become a RouteChef partner" : mode === "success" ? "You're in" : "Partner sign in"}
              </h2>
              <p className="m-0 text-xs text-slate-500">For travel agents and booking partners</p>
            </div>
          </div>
          {mode !== "success" && (
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          )}
        </div>

        {note && mode !== "success" && (
          <p className="m-0 mx-5 mt-4 rounded-lg bg-orange-50 px-3 py-2 text-sm text-orange-900">{note}</p>
        )}

        {/* success */}
        {mode === "success" && (
          <div className="px-6 py-10 text-center">
            <CheckCircle2 size={52} className="mx-auto text-green-600" />
            <h3 className="mt-4 text-lg font-bold text-slate-900">Your partner login is created successfully</h3>
            <p className="mt-2 text-sm text-slate-600">
              Your user ID is <span className="font-semibold text-slate-900">{createdId}</span>.
              <br />
              Taking you to your dashboard…
            </p>
            <Loader2 size={20} className="mx-auto mt-5 animate-spin text-[#FF4E01]" />
          </div>
        )}

        {/* sign in */}
        {mode === "signin" && (
          <form onSubmit={handleSignin} className="space-y-4 px-5 py-5" noValidate>
            <Honeypot value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
            <Field label="User ID or email">
              <input
                className={inputCls}
                value={signin.identifier}
                onChange={(e) => setSignin((f) => ({ ...f, identifier: e.target.value }))}
                autoComplete="username"
                placeholder="ramesh.kulkarni32 or ramesh.kulkarni32@gmail.com"
                autoFocus
              />
            </Field>
            <Field label="Password">
              <PasswordInput
                value={signin.password}
                onChange={(e) => setSignin((f) => ({ ...f, password: e.target.value }))}
                autoComplete="current-password"
              />
            </Field>
            <Captcha onToken={setCaptchaToken} resetKey={`in-${captchaReset}`} />
            {serverError && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{serverError}</p>}
            <button
              type="submit"
              disabled={busy}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#FF4E01] py-3 text-[15px] font-semibold text-white hover:bg-[#e84600] disabled:opacity-60"
            >
              {busy && <Loader2 size={18} className="animate-spin" />} Sign in
            </button>
            <p className="text-center text-sm text-slate-600">
              New partner?{" "}
              <button type="button" onClick={() => switchMode("signup")} className="font-semibold text-[#FF4E01] hover:underline">
                Create an account
              </button>
            </p>
          </form>
        )}

        {/* sign up */}
        {mode === "signup" && (
          <form onSubmit={handleSignup} className="space-y-3.5 px-5 py-5" noValidate>
            <Honeypot value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
            <Field label="Full name" error={errors.name}>
              <input className={inputCls} value={signup.name} onChange={setS("name")} autoComplete="name" autoFocus />
            </Field>
            <Field label="Email" error={errors.email}>
              <input
                className={inputCls}
                type="email"
                value={signup.email}
                onChange={setS("email")}
                autoComplete="email"
                placeholder="you@example.com"
              />
              {previewId && !errors.email && (
                <span className="mt-1 block text-xs text-slate-500">
                  Your user ID will be <span className="font-semibold text-slate-700">{previewId}</span>
                </span>
              )}
            </Field>
            <Field label="Mobile number" error={errors.phone}>
              <PhoneField
                className={inputCls}
                selectClassName={inputCls}
                value={signup.phone}
                onChange={(v) => { setSignup((s) => ({ ...s, phone: v })); setErrors((er) => ({ ...er, phone: undefined })); }}
              />
            </Field>

            <label className="flex cursor-pointer items-center gap-2.5 rounded-lg border border-slate-200 px-3 py-2.5">
              <input
                type="checkbox"
                checked={signup.is_registered_business}
                onChange={setS("is_registered_business")}
                className="h-4 w-4 accent-[#FF4E01]"
              />
              <span className="text-sm font-medium text-slate-700">I run a registered business</span>
            </label>
            {signup.is_registered_business && (
              <Field label="Official business name" error={errors.business_name}>
                <input className={inputCls} value={signup.business_name} onChange={setS("business_name")} autoComplete="organization" />
              </Field>
            )}

            <Field label="Password" error={errors.password}>
              <PasswordInput
                value={signup.password}
                onChange={setS("password")}
                autoComplete="new-password"
                placeholder="At least 8 characters"
              />
            </Field>
            <Field label="Approx. annual turnover" optional>
              <select className={inputCls} value={signup.annual_turnover} onChange={setS("annual_turnover")}>
                {TURNOVER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Anything else you'd like us to know" optional>
              <textarea
                className={inputCls + " min-h-[76px] resize-y"}
                value={signup.notes}
                onChange={setS("notes")}
                maxLength={1000}
                placeholder="Cities you serve, typical bookings per month, etc."
              />
            </Field>

            <Captcha onToken={setCaptchaToken} resetKey={`up-${captchaReset}`} />
            {serverError && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{serverError}</p>}

            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={() => switchMode("signin")}
                disabled={busy}
                className="flex-1 rounded-lg border border-slate-300 py-3 text-[15px] font-semibold text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={busy}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-[#FF4E01] py-3 text-[15px] font-semibold text-white hover:bg-[#e84600] disabled:opacity-60"
              >
                {busy && <Loader2 size={18} className="animate-spin" />} Submit
              </button>
            </div>
            <p className="text-center text-xs text-slate-500">
              Already a partner?{" "}
              <button type="button" onClick={() => switchMode("signin")} className="font-semibold text-[#FF4E01] hover:underline">
                Sign in
              </button>
            </p>
          </form>
        )}
      </div>
    </div>,
    document.body
  );
}
